import { EventEmitter, inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { compress, decompress } from 'lz-string';
import { BehaviorSubject, Observable, shareReplay, startWith } from 'rxjs';
import SignaturePad from 'signature_pad';
import { LikutTaskScanHistoryComponent } from '../components/likut-task/likut-task-scan-history/likut-task-scan-history.component';
import { LikutProductDisplayNewRoute } from '../components/likut-task/likut-task.component';
import {
	LikutAssignmentUpdateLineNum,
	LikutConcentratedLine,
	LikutDisplayMain,
	LikutFinishBodyNewRoutes,
	LikutFinishBodyNewRoutesSubTable,
	LikutKabalaStatus,
	LikutKabalaSubSQL,
	LikutMainSQL,
	LikutMekabelDisplay,
	LikutMekabelFinishBody,
	LikutMekabelFinishBodyForUnexistingTask,
	LikutMekabelTaskExtra,
	LikutStatus,
	LikutSubSQL,
	LikutTaskDisplayWithAssignment,
	LStatuses,
	LStatusesDict,
	MekabelWarehouse,
	NewLikutMekabelWarehouseFormResult,
	QuantitiesMatchDict,
} from '../Interfaces/likut';
import { AllProducts, DocTypesNumToName } from '../Interfaces/SQL-morning-responses';
import { AdminTypes, Users } from '../Interfaces/users';
import { getOnlyDistinctValues } from '../Utility/functions';
import { StoreService } from './store.service';
import { TextService } from './text.service';
import { FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { translateDateForSQLServer } from '../Utility/date_functions/SQL_ISO_adapter';
import { LIKUT_KABALA_ORDER_DOCTYPES, LIKUT_KABALA_RETURN_DOCTYPES, LIKUT_SFIRA_DOCTYPE } from '@utils/constants-doctype';
import { LikutSfiraPayload } from '@interfaces/likut-sfira';

export const LIKUT_ADMIN_TYPES = ['melaket', 'melaketm', 'melaketa', 'melaketf', 'checker', 'checkerm', 'likutmanager', 'mekabel', 'sfira'] as const;

export const userAdminToValue = {
	isMelaketAgent: 'melaket',
	isMelaketAgentAndCanGoOverQuantities: 'melaketm',
	isMelaketAgentAndCanAddNewItems: 'melaketa',
	isMelaketAgentAndCanFilterAllTasks: 'melaketf',
	isCheckerAgent: 'checker',
	isCheckerAndCanEdit: 'checkerm',
	isLikutManager: 'likutmanager',
	isMekabel: 'mekabel',
	isSfira: 'sfira',
} as const;

export const adminToHebrewName = {
	melaket: 'מלקט',
	melaketm: 'מלקט',
	melaketa: 'מלקט',
	melaketf: 'מלקט',
	checker: 'בודק',
	checkerm: 'בודק',
	likutmanager: 'מנהל ליקוט',
	mekabel: 'מקבל',
	sfira: 'ספירת מלאי',
} as const;

export const DEFAULT_DOCTYPE_MEKABEL = { num: 11, name: 'קבלה למחסן' } as const;

type LikutMekabelOpened = {
	main: LikutMekabelDisplay;
	extras: LikutMekabelTaskExtra;
};

@Injectable({
	providedIn: 'root',
})
export class LikutService {
	private _ss = inject(StoreService);
	private _text = inject(TextService);
	private router = inject(Router);
	private dialog = inject(MatDialog);
	private fb = inject(FormBuilder);
	constructor() {
		this.handleLikutInit();
	}
	likutOpenedNewRoutesState: { sub: LikutSubSQL[]; task: LikutDisplayMain } = {
		sub: [],
		task: { clientid: '', clientName: '', status: '0', teudot: {}, totalRow: 0, totalTeudot: 0, address: '', city: '', quantitiesMatch: 1 },
	};
	likutMekabelOpenedState: LikutMekabelOpened = {
		main: {
			clientid: '',
			clientName: '',
			status: '0',
			rows: [],
			totalRow: 0,
			supplier: {
				name: '',
				id: '',
			},
			warehouse: {
				name: '',
				id: '',
			},
			doctype: { num: null, name: '' },
			refrenceNumber: '',
		},
		extras: { signature: '', image: '', name: '', remark: '' },
	};

	barcodeForScan$ = new BehaviorSubject<string>('');

	Admin: AdminTypes | null = localStorage.getItem('Admin') as AdminTypes | null;

	likutAdminTypes: Record<typeof LIKUT_ADMIN_TYPES[number], boolean> = LIKUT_ADMIN_TYPES.reduce((p, c) => {
		p[c] = false;
		return p;
	}, {} as Record<typeof LIKUT_ADMIN_TYPES[number], boolean>);

	canSeeManageOrder = false;

	canSeeManageCertainStatuses = false;

	canSeeManageAndAssignLikut = false;

	canSeeManageNotFulfilled = false;

	canSeeDashboard = false;

	canSeeNoStatus = false;

	canSeeAssignUsersKanban = false;

	canSeeConcentratedReport = false;

	canSeeLikutWarehouseInventoryReport = false;

	canSeeLikutWarehouseAllInventoryReport = false;

	canSeeLikutWarehousePalletMovementReport = false;

	canSeeLikutWarehouseManageWarehouses = false;

	canSeeLikutWarehouseDateRangePalletMovementReport = false;

	canSeeLikutScanBarcodeDefinitionManagement = false;
	canSeeLikutScanBarcodeProductsManagement = false;

	canSeeTeudaPrice = false;
	showLikutExtra1InParenthesis = false;
	dontShowParenthesisAtAll = false;
	refreshStockOnTaskEntry = false;
	getBarcodeFromSubTable = false;
	showTeudaItemRemark = false;
	groupOnlyByID = false;
	hideCheckboxAndPencil = false;
	sortByMatchedQuantities = false;

	hasScannerBehavior = false;

	timeSfiraStarted: Date | null = null;
	sfiraWarehouseSelected$ = new BehaviorSubject<MekabelWarehouse | null>(null);

	private readonly pathEntry = 'mobil/likut';

	isLikutAppActive$ = new BehaviorSubject(false);
	isLikutAppActiveWithAlternateRoutes$ = new BehaviorSubject(false);

	usersForAssignment$ = new BehaviorSubject<Users[]>([]);

	likutMekabelDoctypes$ = new BehaviorSubject<DocTypesNumToName[]>([]);
	likutMekabelWarehouses$ = new BehaviorSubject<MekabelWarehouse[]>([]);

	likutWarehouseFormGroup: FormGroup;
	likutCheckboxStateFormGroup: FormGroup;

	filters$ = new BehaviorSubject<string[]>([]);
	filters2$ = new BehaviorSubject<string[]>([]);
	filterFormControl = new FormControl<string[]>([], { nonNullable: true });
	// TODO refactor to remove shareReplay, it can cause memory leaks
	filterFormControl$: Observable<string[]> = this.filterFormControl.valueChanges.pipe(startWith([]), shareReplay(1));

	taskAssignmentPopupDateChange = new EventEmitter<{ task: LikutTaskDisplayWithAssignment; newDate: string }>();
	taskAssignmentPopupAgentChange = new EventEmitter<{ task: LikutTaskDisplayWithAssignment; newAgent: string }>();
	taskAssignmentPopupCancelTask = new EventEmitter<LikutTaskDisplayWithAssignment>();

	uniqueText: Partial<typeof this._text.store> = {
		homeScreenAction: 'התחל ליקוט',
		mainTasks: `משימות ליקוט`,
		teudaName: `תעודת ליקוט`,
		collected: `לוקט`,
		quantityCollected: { '0': `יחידות שלוקטו`, '0-2': 'אריזות שלוקטו', '0-3': 'מחיר מעודכן', '0-4': '', '1': 'כמות שלוקטה', '2': 'כמות שלוקטה' },
		name: {
			'1': 'שם מלקט',
			'2': 'שם בודק',
			'3': 'שם מטפל',
		},
		returnTask: 'החזרה לליקוט',
		returnTaskConfirmation: 'האם להחזיר לליקוט משימה זו?',
		manageTaskReportTitle: 'היסטוריה הזמנות ליקוט',
		manageCertainStatusReportTitle: 'הזמנות לטיפול',
		manageNotFulfilled: 'לא לוקט',
		manageReportTableHeaders1: ['תעודה', 'שם לקוח', 'שם מלקט', 'תאריך', 'שעה', 'שורות', 'סטטוס', 'אשר', 'בחר מלקט'].map(text => ({ text, element: 'th' })),
		taskUpdatedSuccess: 'משימות ליקוט עודכנו בהצלחה',
		taskAssignmentReport: 'שיוך ליקוטים',
		manageNoStatus: 'הזמנות לליקוט',
	};

	get canGoOverQuantities() {
		return (
			this.likutAdminState(userAdminToValue.isMelaketAgentAndCanGoOverQuantities) ||
			this.likutAdminState(userAdminToValue.isMelaketAgentAndCanAddNewItems) ||
			this.likutAdminState(userAdminToValue.isMelaketAgentAndCanFilterAllTasks) ||
			this.likutAdminState(userAdminToValue.isCheckerAndCanEdit)
		);
	}

	get isMelaketAgent() {
		return this.likutAdminState(userAdminToValue.isMelaketAgent);
	}
	get isMelaketAgentAndCanGoOverQuantities() {
		return this.likutAdminState(userAdminToValue.isMelaketAgentAndCanGoOverQuantities);
	}
	get isMelaketAgentAndCanAddNewItems() {
		return this.likutAdminState(userAdminToValue.isMelaketAgentAndCanAddNewItems);
	}
	get isMelaketAgentAndCanFilterAllTasks() {
		return this.likutAdminState(userAdminToValue.isMelaketAgentAndCanFilterAllTasks);
	}

	get isCheckerAgent() {
		return this.likutAdminState(userAdminToValue.isCheckerAgent);
	}

	get isCheckerAgentAndCanEdit() {
		return this.likutAdminState(userAdminToValue.isCheckerAndCanEdit);
	}

	get isLikutManager() {
		return this.likutAdminState(userAdminToValue.isLikutManager);
	}

	get isMekabel() {
		return this.likutAdminState(userAdminToValue.isMekabel);
	}

	get isSfira() {
		return this.likutAdminState(userAdminToValue.isSfira);
	}

	private updateAdminType() {
		this.Admin ??= localStorage.getItem('Admin') as AdminTypes;

		if (!this.Admin) {
			return;
		}

		for (const admin of LIKUT_ADMIN_TYPES) {
			const isAdmin = this.isAdminLowerCase(admin);
			this.likutAdminTypes[admin] = isAdmin;
		}

		this.isLikutAppActive$.next(Object.values(this.likutAdminTypes).some(admin => admin));

		if (this.isLikutAppActive$.value) {
			this.isLikutAppActiveWithAlternateRoutes$.next(true);
		}
	}

	private isAdminLowerCase(value: typeof LIKUT_ADMIN_TYPES[number]) {
		// 'melaketM' includes both melaket AND melaketm
		if (!this.Admin) return false;
		return this.Admin.toLowerCase().includes(value);
	}

	private likutAdminState(propertyToLookup: typeof LIKUT_ADMIN_TYPES[number]) {
		const val = this.likutAdminTypes[propertyToLookup];
		if (!this.isLikutAppActive$.value) {
			this.isLikutAppActive$.next(val);
		}
		return val;
	}

	refreshAllAdminTypes() {
		this.updateAdminType();
	}

	handleSetupCanSeeReportsBasedOnCustomerInformation() {
		if (!this._ss.CustomerInformation?.LikutReportsCanSee) return;

		const reportsSplit: string[] = this._ss.CustomerInformation.LikutReportsCanSee.split(',');

		this.canSeeManageOrder ||= reportsSplit.includes('0');
		this.canSeeManageCertainStatuses ||= reportsSplit.includes('1');
		this.canSeeManageAndAssignLikut ||= reportsSplit.includes('2');
		this.canSeeManageNotFulfilled ||= reportsSplit.includes('3');
		this.canSeeDashboard ||= reportsSplit.includes('4');
		this.canSeeNoStatus ||= reportsSplit.includes('5');
		this.canSeeAssignUsersKanban ||= reportsSplit.includes('6');
		this.canSeeConcentratedReport ||= reportsSplit.includes('7');
		this.canSeeLikutWarehouseInventoryReport ||= reportsSplit.includes('8');
		this.canSeeLikutWarehouseAllInventoryReport ||= reportsSplit.includes('9');
		this.canSeeLikutWarehousePalletMovementReport ||= reportsSplit.includes('10');
		this.canSeeLikutWarehouseManageWarehouses ||= reportsSplit.includes('11');
		this.canSeeLikutWarehouseDateRangePalletMovementReport ||= reportsSplit.includes('12');
		this.canSeeLikutScanBarcodeDefinitionManagement ||= reportsSplit.includes('13');
		this.canSeeLikutScanBarcodeProductsManagement ||= reportsSplit.includes('14');

		// if (environment.development) {
		// 	this.canSeeLikutScanBarcodeProductsManagement = true;
		// 	this.canSeeLikutScanBarcodeDefinitionManagement = true;
		// 	FormattedLog.log({ area: 'LikutService', message: 'Development environment, enabling canSeeLikutScanBarcodeProductsManagement, canSeeLikutScanBarcodeDefinitionManagement' });
		// }
	}

	handleLikutInit() {
		this.refreshAllAdminTypes();
		this.handleSetupCanSeeReportsBasedOnCustomerInformation();
		this.populateLikutExtraParameters();
		this.sfiraWarehouseSelected$.next(null);
		this.timeSfiraStarted = null;
		if (this.isLikutAppActive$.value) {
			this.replaceTextForLikut();
		}
	}

	populateLikutExtraParameters() {
		if (!this._ss.CustomerInformation) return;

		if (this._ss.CustomerInformation.LikutRemarkExtra1) {
			this.showLikutExtra1InParenthesis = this._ss.CustomerInformation.LikutRemarkExtra1 === '1';
			this.dontShowParenthesisAtAll = this._ss.CustomerInformation.LikutRemarkExtra1 === '2';
		}

		if (this._ss.CustomerInformation.LikutRefreshStock) {
			const split = this._ss.CustomerInformation.LikutRefreshStock.split('_');
			this.refreshStockOnTaskEntry = split.includes('1');
		}
		this.canSeeTeudaPrice = this._ss.CustomerInformation.LikutPrice === '1';

		this.getBarcodeFromSubTable = this._ss.CustomerInformation.LikutBarcode === '1';

		this.hasScannerBehavior = this._ss.CustomerInformation.LikutScanner !== '0';

		this.showTeudaItemRemark = this._ss.CustomerInformation.LikutItemRemark === '1';

		this.groupOnlyByID = this._ss.CustomerInformation.LikutGroupOnlyByID === '1';

		this.hideCheckboxAndPencil = this._ss.CustomerInformation.LikutHideCheckboxAndPencil === '1';

		this.sortByMatchedQuantities = this._ss.CustomerInformation.LikutSortTask === '1';
	}

	private replaceTextForLikut() {
		this.handlePopulateMekabelText();
		this._text.updateTextStore(this.uniqueText);
	}

	buildLikutFinishPayloadBody(formValues: { name: string; remarks: string }, signaturePad: SignaturePad, taskStatus: LikutStatus) {
		const { name, remarks } = formValues;

		let body: LikutFinishBodyNewRoutes[];

		const teudotArray = this.likutOpenedNewRoutesState.sub;
		// const scanHistory = this.scans$.value.reduce((p, c) => {
		// 	p[c.barcode] ||= [];
		// 	p[c.barcode].push(c);
		// 	return p;
		// }, {} as Record<string, ScanHistoryItem[]>);

		body = teudotArray.map(likut => ({
			table: 'sub',
			TeudaNum: likut.TeudaNum,
			TeudaType: likut.TeudaType,
			ProductId: likut.ProductId,
			LStatus: likut.LStatus === LStatusesDict.manuallyAdded ? LStatusesDict.manuallyAdded : String(Number(taskStatus) + 1),
			LikutPackQty: likut.LikutPackQty || 0 /*+  (scanHistory[likut.Barcode]?.reduce((p, c) => p + c.quantity, 0) || 0) */,
			LikutQty: likut.LikutQty || 0 /* + (scanHistory[likut.Barcode]?.reduce((p, c) => p + c.weight, 0) || 0) */,
			LikutPrice: likut.LikutPrice || 0,
			TeudaLineNum: String(likut.TeudaLineNum),
		}));

		let QuantitiesMatch: 0 | 1 | 2 = !teudotArray.some(teuda => teuda.TeudaQuantity !== teuda.LikutQty || teuda.TeudaPackQuantity !== teuda.LikutPackQty)
			? QuantitiesMatchDict.notMatch
			: QuantitiesMatchDict.match;

		const anyAddedProducts = teudotArray.some(teuda => teuda.LStatus === LStatusesDict.manuallyAdded);

		if (anyAddedProducts) {
			QuantitiesMatch = QuantitiesMatchDict.addedProducts;
		}

		//TODO dont mutate original quantitiesMatch ( keep as 2 )

		const taskBody: LikutFinishBodyNewRoutes[] = Object.values(this.likutOpenedNewRoutesState.task.teudot).map(task => ({
			table: 'main',
			TeudaNum: task.TeudaNum,
			TeudaType: task.TeudaType,
			MelaketName: this.isMelaketAgent ? name || this._ss.UserNameConnected : task.MelaketName,
			CheckerName: this.isCheckerAgent ? name || this._ss.UserNameConnected : '',
			LikutSignature: this.isMelaketAgent ? signaturePad.toDataURL() : task.LikutSignature || '',
			CheckerSignature: this.isCheckerAgent ? signaturePad.toDataURL() : '',
			LikutCheckDate: this.isCheckerAgent ? translateDateForSQLServer().toISOString() : '',
			LikutDate: this.isMelaketAgent ? translateDateForSQLServer().toISOString() : task.LikutDate || translateDateForSQLServer().toISOString(),
			MelaketRemark: this.isMelaketAgent ? remarks : task.MelaketRemark,
			CheckerRemark: this.isCheckerAgent ? remarks : '',
			LikutStatus: String(Number(taskStatus) + 1) as LikutStatus,
			QuantitiesMatch: QuantitiesMatch,
		}));

		return body.concat(taskBody);
	}
	buildLikutFinishWithoutScansPayloadBody(formValues: { name: string; remarks: string }, signaturePad: SignaturePad, taskStatus: LikutStatus): LikutFinishBodyNewRoutes[] {
		const { name, remarks } = formValues;

		let body: LikutFinishBodyNewRoutes[];

		const teudotArray = this.likutOpenedNewRoutesState.sub;

		body = this.buildLikutFinishSubTablePayloadBody(teudotArray, taskStatus);

		let QuantitiesMatch: 0 | 1 | 2 = !teudotArray.some(teuda => teuda.TeudaQuantity !== teuda.LikutQty || teuda.TeudaPackQuantity !== teuda.LikutPackQty)
			? QuantitiesMatchDict.notMatch
			: QuantitiesMatchDict.match;

		const anyAddedProducts = teudotArray.some(teuda => teuda.LStatus === LStatusesDict.manuallyAdded);

		if (anyAddedProducts) {
			QuantitiesMatch = QuantitiesMatchDict.addedProducts;
		}

		const taskBody: LikutFinishBodyNewRoutes[] = Object.values(this.likutOpenedNewRoutesState.task.teudot).map(task => ({
			table: 'main',
			TeudaNum: task.TeudaNum,
			TeudaType: task.TeudaType,
			MelaketName: this.isMelaketAgent ? name || this._ss.UserNameConnected : task.MelaketName,
			CheckerName: this.isCheckerAgent ? name || this._ss.UserNameConnected : '',
			LikutSignature: this.isMelaketAgent ? signaturePad.toDataURL() : task.LikutSignature || '',
			CheckerSignature: this.isCheckerAgent ? signaturePad.toDataURL() : '',
			LikutCheckDate: this.isCheckerAgent ? translateDateForSQLServer().toISOString() : '',
			LikutDate: this.isMelaketAgent ? translateDateForSQLServer().toISOString() : task.LikutDate || translateDateForSQLServer().toISOString(),
			MelaketRemark: this.isMelaketAgent ? remarks : task.MelaketRemark,
			CheckerRemark: this.isCheckerAgent ? remarks : '',
			LikutStatus: String(Number(taskStatus) + 1) as LikutStatus,
			QuantitiesMatch,
		}));

		return body.concat(taskBody);
	}

	buildLikutFinishSubTablePayloadBody(likutRows: LikutSubSQL[], taskStatus: LikutStatus): (LikutFinishBodyNewRoutesSubTable & { TeudaType: number; TeudaNum: string })[] {
		return likutRows.map(likut => ({
			table: 'sub',
			TeudaNum: likut.TeudaNum,
			TeudaType: likut.TeudaType,
			ProductId: likut.ProductId,
			LStatus: likut.LStatus === LStatusesDict.manuallyAdded ? LStatusesDict.manuallyAdded : String(Number(taskStatus) + 1),
			LikutPackQty: likut.LikutPackQty || 0,
			LikutQty: likut.LikutQty || 0,
			LikutPrice: likut.LikutPrice || 0,
			TeudaLineNum: String(likut.TeudaLineNum),
			ID: likut.ID,
			DocID: likut.DocID,
		}));
	}

	buildLikutSfiraFinishPayload(): LikutSfiraPayload[] {
		const { sub, task } = this.likutOpenedNewRoutesState;

		const body: LikutSfiraPayload[] = sub.map(row => ({
			table: 'sub',
			Eitur: row.Eitur,
			LikutPackQty: row.LikutPackQty || 0,
			LikutQty: row.LikutQty || 0,
			ProductID: row.ProductId,
			LineNum: row.TeudaLineNum,
		}));

		const main: LikutSfiraPayload = {
			table: 'main',
			AgentID: this._ss.AgentIdConnected,
			CreateStart: translateDateForSQLServer(this.timeSfiraStarted),
			WarehouseID: this.sfiraWarehouseSelected$.value.WarehouseId,
		};

		body.push(main);

		return body;
	}

	buildLikutMekabelFinishPayloadBody(formValues: { name: string; remarks: string }, signaturePad: SignaturePad, taskStatus: LikutKabalaStatus) {
		const { name, remarks } = formValues;

		let body: LikutMekabelFinishBody[];

		const teudotArray = Object.values(this.likutMekabelOpenedState.main.rows);
		const rowsWithWarehouseSelected = this.likutWarehouseFormGroup.value;

		body = teudotArray.map(likut => ({
			table: 'sub',
			TeudaNum: likut.TeudaNum,
			TeudaType: likut.TeudaType,
			DiscountOut: likut.DiscountOut || 0,
			Eitur: likut.Eitur || '',
			ProductId: likut.ProductId,
			ItemRemark: likut.ItemRemark || '',
			ItemStatus: String(Number(taskStatus) + 1),
			PriceOut: likut.PriceOut || 0,
			TeudaPackQtyKabala: likut.TeudaPackQtyKabala || 0,
			TeudaQtyKabala: likut.TeudaQtyKabala || 0,
			TeudaLineNum: likut.TeudaLineNum,
			Warehouse: rowsWithWarehouseSelected[this.getLineIdentifier(likut)] || '',
		}));

		const { clientid, doctype, refrenceNumber, status } = this.likutMekabelOpenedState.main;

		const taskBody: LikutMekabelFinishBody = {
			table: 'main',
			TeudaNum: refrenceNumber,
			TeudaType: doctype.num,
			DATAString: JSON.stringify(this.likutMekabelOpenedState),
			ImageFileName: this.likutMekabelOpenedState.extras.image,
			MekabelId: this._ss.AgentIdConnected,
			MekabelName: name || this._ss.UserNameConnected,
			Remark: remarks,
			SignatureString: signaturePad.toDataURL(),
			TeudaKabalaDate: translateDateForSQLServer(new Date()).toISOString(),
			TeudaStatus: String(Number(taskStatus) + 1) as LikutKabalaStatus,
		};

		body = body.concat(taskBody);

		return body;
	}

	buildLikutMekabelFinishPayloadBodyForNewTask(formValues: { name: string; remarks: string; teudaReference: string }, signaturePad: SignaturePad, taskStatus: LikutKabalaStatus) {
		const { name, remarks, teudaReference } = formValues;

		let body: LikutMekabelFinishBodyForUnexistingTask[];

		const teudotArray = Object.values(this.likutMekabelOpenedState.main.rows);

		body = teudotArray.map(likut => ({
			table: 'sub',
			...likut,
			ItemStatus: String(Number(taskStatus) + 1),
		}));

		const { clientid, doctype, refrenceNumber, clientName, warehouse } = this.likutMekabelOpenedState.main;

		const taskBody: LikutMekabelFinishBodyForUnexistingTask = {
			table: 'main',
			TeudaNum: refrenceNumber,
			TeudaType: doctype.num,
			DATAString: JSON.stringify(this.likutMekabelOpenedState),
			ImageFileName: this.likutMekabelOpenedState.extras.image,
			MekabelId: this._ss.AgentIdConnected,
			MekabelName: name || this._ss.UserNameConnected,
			Remark: remarks,
			SignatureString: signaturePad.toDataURL(),
			TeudaKabalaDate: translateDateForSQLServer(new Date()).toISOString(),
			TeudaStatus: String(Number(taskStatus) + 1) as LikutKabalaStatus,
			Client_Id: clientid,
			Client_Name: clientName,
			Currency: '',
			Extra1: '',
			Extra2: '',
			Extra3: '',
			Extra4: '',
			Extra5: '',
			PrintStatus: 0,
			Rate: 0,
			StringNum: '',
			TeudaAgent: this._ss.AgentIdConnected,
			TeudaCompany: 1,
			TeudaDate: translateDateForSQLServer(new Date()).toISOString(),
			TeudaErpErr: '',
			TeudaErpNum: '',
			TotalLines: teudotArray.length,
			Warehouse: warehouse.id,
			Warehouse2: warehouse.id,
			TeudaReference: teudaReference,
		};

		return body.concat(taskBody);
	}

	resetLikutState() {
		// this.scans$.next([]);
		this.barcodeForScan$.next('');
		this.likutOpenedNewRoutesState = {
			sub: [],
			task: { clientid: '', clientName: '', status: '0', teudot: {}, totalRow: 0, totalTeudot: 0, address: '', city: '', quantitiesMatch: 1 },
		};

		this.likutMekabelOpenedState = {
			main: {
				clientid: '',
				clientName: '',
				status: '0',
				rows: [],
				totalRow: 0,
				supplier: {
					name: '',
					id: '',
				},
				warehouse: {
					name: '',
					id: '',
				},
				doctype: null,
				refrenceNumber: '',
			},
			extras: { signature: '', image: '', name: '', remark: '' },
		};

		this.likutWarehouseFormGroup = this.fb.group({});
		this.likutCheckboxStateFormGroup = this.fb.group({});
	}

	private getLikutUsersForKanban() {
		let path = '';

		path = 'get-all-users-for-assignment-kanban';

		return this._ss.get<Users>(`${this.pathEntry}/${path}`);
	}

	updateLikutUsersForAssignment() {
		this.getLikutUsersForKanban().subscribe({
			next: res => {
				if (typeof res === 'string') {
					return;
				}

				this.usersForAssignment$.next(res.recordset);
			},
		});
	}

	handlePopulateMekabelText() {
		if (!this.isMekabel) return;

		const textOverride: Partial<typeof this.uniqueText> = {
			teudaName: 'תעודת קבלה למחסן',
			quantityCollected: { '0': 'יחידות שהתקבלו', '0-2': 'אריזות שהתקבלו', '0-3': 'הנחה', '0-4': 'מחיר', '1': 'כמות שהתקבלה', '2': 'כמות שהתקבלה' },
			name: {
				'1': 'שם מקבל',
				'2': 'שם בודק',
				'3': 'שם מטפל',
			},
			collected: 'התקבל',
		};

		this.uniqueText = { ...this.uniqueText, ...textOverride };
	}

	async handleFileUploadLikutMekabel(file: File) {
		const fd = new FormData();

		fd.append(file.name, file);

		try {
			const res = await fetch(`${this._ss.CustomerInformation.CustServer}/${this.pathEntry}/upload-camera-picture-mekabel`, {
				method: 'POST',
				body: fd,
			});

			if (!res.ok) {
				const data = await res.json();
				console.log(data);
				return false;
			}

			this.likutMekabelOpenedState.extras.image = file.name;

			console.log('finished fetch');
			return true;
		} catch (error) {
			console.log(error);
			return false;
		}
	}

	handleUpdateOpenMekabelTaskWithSupplierAndWarehouseAndResolveIfShouldRouteToLikutMekabel(result: NewLikutMekabelWarehouseFormResult | 'cancel' | undefined) {
		if (result === 'cancel' || !result) return;

		const boolean = this.updateOpenMekabelTaskWithSupplierAndWarehouse(result);

		if (boolean) {
			this.router.navigate(['likut', 'tasks', 'mekabel'], { queryParams: { new: true } });
		}
	}

	private updateOpenMekabelTaskWithSupplierAndWarehouse(result: NewLikutMekabelWarehouseFormResult) {
		const { warehouse, supplier } = result;

		this.likutMekabelOpenedState = {
			main: {
				...this.likutMekabelOpenedState.main,
				warehouse: { id: warehouse.WarehouseId, name: warehouse.WarehouseName },
				supplier: { id: supplier.ClientId, name: supplier.Client_Name },
				clientid: supplier.ClientId,
				clientName: supplier.Client_Name,
				doctype: { ...DEFAULT_DOCTYPE_MEKABEL },
				refrenceNumber: `${new Date().getTime().toString()}000${this._ss.AgentIdConnected}`,
			},
			extras: this.likutMekabelOpenedState.extras,
		};

		return true;
	}

	async setupTestingLikutMekabel() {
		this.likutMekabelOpenedState = {
			main: {
				...this.likutMekabelOpenedState.main,
				warehouse: { id: '1', name: 'מחסן 1' },
				supplier: { id: '9007', name: 'סופר אגמים נתניה (שותפות אגמים לקמעונאות גרנות)' },
				clientid: '9007',
				clientName: 'סופר אגמים נתניה (שותפות אגמים לקמעונאות גרנות)',
				doctype: { ...DEFAULT_DOCTYPE_MEKABEL },
				refrenceNumber: `${new Date().getTime().toString()}000${this._ss.AgentIdConnected}`,
			},
			extras: this.likutMekabelOpenedState.extras,
		};

		const products = localStorage.getItem('AllProducts');

		if (!products) {
			try {
				await new Promise<void>((resolve, reject) => {
					this._ss.GetAllProducts().subscribe({
						next: res => {
							if (typeof res === 'string') {
								return reject();
							}

							localStorage.setItem('AllProducts', compress(JSON.stringify(res.recordset)));

							resolve();
						},
					});
				});
			} catch (error) {
				alert('תקלה בשרת, לא עודכנו מוצרים חדשים');
			}
		}

		this.Admin = 'Mekabel';

		this.router.navigate(['likut', 'tasks', 'mekabel'], { queryParams: { new: true } });
	}

	handleGenerateProductDisplayFromAllProducts(input: { product: AllProducts; manuallyAdded: boolean; forcedLineNum?: number }): LikutProductDisplayNewRoute {
		const { TeudaNum, TeudaType, ID } = Object.values(this.likutOpenedNewRoutesState.task.teudot)[0];
		const { product, manuallyAdded, forcedLineNum } = input;
		const TeudaLineNum = forcedLineNum ?? this.likutOpenedNewRoutesState.sub.length + 1;
		const likutSubID = `${TeudaType}${TeudaNum}${TeudaLineNum}`;
		return {
			opened: true,
			likut: {
				ID: likutSubID,
				TeudaNum,
				DocID: ID,
				TeudaType,
				TeudaItemRemark: '',
				PicName: product.ItemPicName,
				ProductId: product.ProductId,
				ProductName: product.Product_Name,
				Stock: product.Stock,
				TeudaBonusPackQuantity: 0,
				TeudaBonusQuantity: 0,
				TeudaLineNum,
				TeudaPackQuantity: 0,
				TeudaQuantity: 0,
				TeudaUnitPerPack: product.ItemPack,
				Amount: 0,
				Arizot: 0,
				ItemPack: product.ItemPack,
				LikutExtra1: '',
				LikutExtra2: '',
				LikutPackQty: 0,
				LikutQty: 0,
				LStatus: manuallyAdded ? LStatusesDict.manuallyAdded : LStatusesDict.regular,
				LikutPrice: 0,
			} as LikutSubSQL,
			product,
		};
	}

	handleOpenScanHistoryPopup(barcode: string) {
		this.barcodeForScan$.next(barcode);
		this.dialog.open(LikutTaskScanHistoryComponent);
	}

	getLineIdentifier(line: LikutSubSQL | LikutProductDisplayNewRoute | LikutKabalaSubSQL) {
		if ('likut' in line) return `${line.likut.TeudaNum}${line.likut.TeudaLineNum}`;
		return `${line.TeudaNum}-${line.TeudaLineNum}`;
	}

	getAllKanbanTasks() {
		return this._ss.get<LikutMainSQL>(`${this.pathEntry}/get-all-for-assignment-kanban`);
	}

	assignKanbanTaskToAgent(body: { TeudaNum: string[]; AssignTo: string; LikutStatus?: string }) {
		const LikutStatus = body.AssignTo ? '1' : '0';
		body.LikutStatus = LikutStatus;
		return this._ss.patch(`${this.pathEntry}/set-assignment-agent-kanban`, body);
	}

	assignKanbanTaskLineNum(lines: LikutAssignmentUpdateLineNum[]) {
		const body = { lines };
		return this._ss.patch(`${this.pathEntry}/set-assignment-linenum-kanban`, body);
	}

	buildFilters(tasks: LikutConcentratedLine[]) {
		if (!tasks.length || !('Filter' in tasks[0])) return;
		this.filters$.next(getOnlyDistinctValues<LikutConcentratedLine, 'Filter'>(tasks, 'Filter').filter(Boolean).map(String));
		this.filters2$.next(getOnlyDistinctValues<LikutConcentratedLine, 'filter2'>(tasks, 'filter2').filter(Boolean).map(String));

		this.filters2$.next(['100', '200']);
	}

	cancelLikutTask(teudaNum: string[]) {
		return this._ss.patchMainAndTaskForLikut('assign-status-from-null-to-cancelled', teudaNum);
	}

	isKabalaTeudaTypeReturn(teudaType: number | string | null): boolean {
		if (teudaType === null) return false;
		const valueAsProperType = +teudaType as typeof LIKUT_KABALA_RETURN_DOCTYPES[number];
		return LIKUT_KABALA_RETURN_DOCTYPES.includes(valueAsProperType);
	}

	isKabalaTeudaTypeOrder(teudaType: number | string | null): boolean {
		if (teudaType === null) return false;
		const valueAsProperType = +teudaType as typeof LIKUT_KABALA_ORDER_DOCTYPES[number];
		return !LIKUT_KABALA_ORDER_DOCTYPES.includes(valueAsProperType);
	}

	getCurrentTeudaType(): number | null {
		if (Object.keys(this.likutOpenedNewRoutesState.task.teudot).length) {
			return Object.values(this.likutOpenedNewRoutesState.task.teudot)[0].TeudaType;
		}

		if (this.likutMekabelOpenedState.main.rows.length) {
			return Object.values(this.likutMekabelOpenedState.main.rows)[0].TeudaType;
		}

		return null;
	}

	refreshLikutMekabelWarehouseFromMemory() {
		if (!this.isMekabel) return;
		const warehouses = localStorage.getItem('LikutMekabelWarehouses');
		if (!warehouses) {
			alert('לא נמצאו מחסנים');
			return;
		}
		this.likutMekabelWarehouses$.next(JSON.parse(decompress(warehouses)));
	}
	refreshLikutCheckboxStateFormGroup() {
		let taskSub: Array<LikutKabalaSubSQL | LikutSubSQL> = this.likutOpenedNewRoutesState.sub;
		if (this.isMekabel) taskSub = this.likutMekabelOpenedState.main.rows;
		const groupFromRows = taskSub.reduce((p, c) => {
			p[this.getLineIdentifier(c)] = this.fb.control(false);
			return p;
		}, {});
		this.likutCheckboxStateFormGroup = this.fb.group(groupFromRows);
	}

	refreshMekabelWarehouseFormGroup() {
		if (!this.isMekabel) return;
		const groupFromRows = this.likutMekabelOpenedState.main.rows.reduce((p, c) => {
			p[this.getLineIdentifier(c)] = [];
			return p;
		}, {});
		this.likutWarehouseFormGroup = this.fb.group(groupFromRows);
	}

	addMekabelWarehouseControl(likut: LikutKabalaSubSQL) {
		const identifier = this.getLineIdentifier(likut);
		this.likutWarehouseFormGroup.addControl(identifier, this.fb.control(null));
	}

	updateSfiraWarehouseSelected(warehouse: MekabelWarehouse) {
		this.sfiraWarehouseSelected$.next(warehouse);
	}

	addCheckboxControl(likut: LikutKabalaSubSQL | LikutSubSQL) {
		const identifier = this.getLineIdentifier(likut);
		this.likutCheckboxStateFormGroup.addControl(identifier, this.fb.control(false));
	}

	getRowsWithWarehouseSelected(): string[] {
		return Object.keys(this.likutWarehouseFormGroup.value).filter(key => this.likutWarehouseFormGroup.value[key]);
	}

	createSfiraOpenedLikutState(): typeof this.likutOpenedNewRoutesState.task & { teudanum: string; teudaType: number } {
		const teudanum = `${new Date().getTime()}000${this._ss.AgentIdConnected}`;
		const teudaType = LIKUT_SFIRA_DOCTYPE;
		const clientid = '0';
		const clientName = 'ספירת מלאי';
		const status = '0';
		const teudot: Record<string, LikutMainSQL> = {};
		const totalRow = 0;
		const totalTeudot = 0;
		const address = '';
		const city = '';
		const quantitiesMatch = 1;

		return { clientid, clientName, status, teudot, totalRow, totalTeudot, address, city, quantitiesMatch, teudanum, teudaType };
	}

	attachDummyLikutTaskForSfira() {
		const dummyTask = this.createSfiraOpenedLikutState();
		this.likutOpenedNewRoutesState.task = dummyTask;
		dummyTask.teudot[dummyTask.teudanum] = {
			ID: '',
			TeudaNum: dummyTask.teudanum,
			TeudaType: dummyTask.teudaType,
			Client_Id: '',
			TeudaCreateDate: '',
			TeudaSupplyDate: '',
			TeudaAgent: '',
			Client_Name: '',
			User_Name: '',
			TuedaErpDocNum: '',
			Address: '',
			City: '',
			TeudaCompany: 1,
			LikutStatus: '0',
			MelaketName: '',
			CheckerName: '',
			LikutExtra1: '',
			LikutExtra2: '',
			LikutExtra3: '',
			LikutDate: '',
			LikutSignature: '',
			CheckerSignature: '',
			LikutCheckDate: '',
			OpenedBy: '',
			CheckerRemark: '',
			MelaketRemark: '',
			AssignedTo: '',
			TotalLines: 0,
			QuantitiesMatch: 1,
			AreaId: '',
			AreaName: '',
			LineNum: 0,
			Color: '',
			Filter: '',
			DocNum: 0,
			DocType: dummyTask.teudaType,
		};
	}

	getFirstAvailableTeudaLineNum(mode: 'likut' | 'mekabel'): number {
		const lineNums = new Set<number>(mode === 'likut' ? this.likutOpenedNewRoutesState.sub.map(sub => sub.TeudaLineNum) : this.likutMekabelOpenedState.main.rows.map(row => row.TeudaLineNum));

		let i = 1;
		while (lineNums.has(i)) {
			i++;
		}
		return i;
	}
}
